import Tagify, {ChangeEventData, ClickEventData} from '@yaireo/tagify';
import {MixedTags} from '@yaireo/tagify/dist/react.tagify';
import React, {FC, useCallback, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {boxedInputColorReaders} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {BoundsRect} from '../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import chevronDownImgSrc from '../../../../assets/images/chevronDown.png';
import {placeCaretAfterNode} from '../../../../helpers/domSelectionHelpers';
import {StringParameter} from '../../../editors/commons/parameterModel';
import {
  hasRegexDisclosure,
  isRegexPart,
  RegexPart,
  StringOrTemplate,
} from '../../../editors/commons/stringTemplateModel';
import {ClientAtom} from '../../../editors/expressions/expressionAtomModel';
import {
  filterAllowedExpressions,
  filterStringExpressions,
} from '../../../editors/expressions/expressionInferHelpers';
import {Expression} from '../../../editors/expressions/expressionModel';
import {ExpressionRef} from '../../../editors/expressions/expressionRefModel';
import {RegexPartSettingsPopover} from './stringTemplate/regexPartSettingsPopover';
import {StringTemplateDropdown} from './stringTemplate/stringTemplateDropdown';
import {
  maybeResetSelection,
  parseTagifyValue,
  renderTagify,
  renderTemplatePart,
  TAGIFY_SETTINGS,
  TemplatePartData,
} from './stringTemplate/tagifyHelpers';
import {TagifyWrapper} from './stringTemplate/tagifyWrapper';

/*
 * Types.
 */

export interface InputRuleStringTemplateParameterProps {
  stringParameter: StringParameter;
  value: StringOrTemplate | undefined;
  isDisabled?: boolean;
  expressions: ReadonlyArray<Expression>;
  atom?: ClientAtom;
  onValueChange: HandlerOf<StringOrTemplate>;
  onSelectCreateExpression?: Handler;
}

/*
 * Styles.
 */

const StyledInputWrapper = styled.div`
  background-color: ${boxedInputColorReaders.background};
  border-radius: 4px;
  display: block;
  min-width: 150px;
  max-width: 500px;
  align-items: center;
  flex-grow: 1;
  position: relative;
  padding: 4px 8px;

  &:hover {
    background-color: ${boxedInputColorReaders.backgroundHover};
  }

  &:focus-within,
  &[focus-within] {
    background-color: ${boxedInputColorReaders.backgroundFocused};
    box-shadow: 0 0 0 1px ${(p) => p.theme.palette.blue.shade40},
      inset 0 0 0 1px ${(p) => p.theme.palette.blue.shade40};
  }
`;

const StyledMixedTags = styled(TagifyWrapper)`
  display: block;

  max-height: 140px;
  overflow-y: auto;

  &.multiline {
    min-height: 75px;
  }

  &.singleline {
    height: auto;
    resize: none;
  }

  &.tagify--mix .tagify__input {
    padding: 0;
    height: 22px;
    line-height: 22px;
  }

  &.singleline .tagify__input {
    white-space: nowrap;
  }

  &.tagify {
    --tags-focus-border-color: transparent;
    --tags-border-color: transparent;
    --tags-hover-border-color: transparent;
    --tag-hide-transition: 0s;
    border: 0;
  }

  .tagify__tag {
    // Make sure that dropdowns do not appear behind tags.
    z-index: 0;

    // Disable "bump" animation.
    & > div:before {
      transition: unset;
      animation: none;
    }

    // Disable hover animation.
    &:hover:not([readonly]) div:before,
    &:focus div:before {
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      box-shadow: 0 0 0 var(--tag-inset-shadow-size, 1.1em) var(--tag-hover) inset;
    }

    .tagify__tag__removeBtn {
      font-size: 16px;
      transition: unset;
      height: 16px;
      width: 16px;
      margin-top: 2px;
    }

    --tag-text-color: ${(p) => p.theme.palette.teal.shade50};
    --tag-remove-btn-color: ${(p) => p.theme.greys.white};

    --tag-bg: ${(p) => p.theme.palette.teal.shade20};
    --tag-hover: ${(p) => p.theme.palette.teal.shade30};
    --tag-remove-bg: ${(p) => p.theme.palette.teal.shade40};
    --tag-remove-btn-bg--hover: transparent;
    /* --tag-pad: 0 4px 0 7px; */
    line-height: 1.5;
    font-weight: 600;

    &[isvalid='false'] {
      --tag-text-color: ${(p) => p.theme.palette.red.shade50};
      --tag-bg: ${(p) => p.theme.palette.red.shade20};
      --tag-hover: ${(p) => p.theme.palette.red.shade30};
      --tag-remove-bg: ${(p) => p.theme.palette.red.shade40};
    }

    &[isregex='true'] {
      --tag-bg: ${(p) => p.theme.palette.blue.shade20};
      --tag-hover: ${(p) => p.theme.palette.blue.shade30};
      --tag-remove-bg: ${(p) => p.theme.palette.blue.shade30};
      --tag-text-color: ${(p) => p.theme.palette.blue.shade40};
      --tag-remove-btn-color: ${(p) => p.theme.palette.blue.shade40};
    }

    &[hasdisclosure='true'] {
      --tag-pad: 0 4px 0 7px;
    }

    &[hasdisclosure='true'] > div:after {
      content: '';
      display: inline-block;
      background-image: url(${chevronDownImgSrc});
      background-size: 100%;
      width: 16px;
      height: 16px;
      margin-left: 2px;
      vertical-align: middle;
    }

    & > div {
      border-radius: 4px;
    }

    // Hide delete button.
    x {
      display: none;
    }
  }
` as typeof MixedTags;

interface EditedRegexPart {
  bounds: BoundsRect;
  part: RegexPart;
  tag: HTMLElement;
}

export const InputStringTemplateParameter: FC<InputRuleStringTemplateParameterProps> = (props) => {
  const {value, stringParameter, atom, expressions, isDisabled, onValueChange, onSelectCreateExpression} =
    props;
  const isMultiline = Boolean(stringParameter.multiline);
  const [editedPart, setEditedPart] = useState<EditedRegexPart | undefined>();
  const tagifyRef = useRef<Tagify<TemplatePartData>>();

  const validExpressions = useMemo(() => buildValidExpressions(atom, expressions), [atom, expressions]);

  const onPartSelected = useCallback(
    (part: ExpressionRef | RegexPart) => {
      if (!tagifyRef.current) {
        return;
      }

      // If the input has never been focused, tagify will incorrectly insert after the trailing <BR>.
      maybeResetSelection(tagifyRef.current);

      const newTag = renderTemplatePart(part, expressions);

      // diff tag elements to find the one that was added.
      // "addTags" is supposed to return it, but instead it returns an empty document-fragment.
      const currentTagElms = tagifyRef.current.getTagElms();
      tagifyRef.current.addTags([newTag]);
      const tagElm = tagifyRef.current.getTagElms().find((elm) => !currentTagElms.includes(elm));

      if (tagElm) {
        placeCaretAfterNode(tagElm);
        const tagsContainer = tagElm.parentElement?.parentElement;
        tagsContainer?.scrollTo({left: tagElm.getBoundingClientRect().right, behavior: 'smooth'});
      }
    },
    [expressions],
  );

  const updateModel = useCallback(
    (event: CustomEvent<ChangeEventData<TemplatePartData>>) => {
      onValueChange(parseTagifyValue(event.detail.value));
    },
    [onValueChange],
  );

  const onClickTag = useCallback(
    (event: CustomEvent<ClickEventData<TemplatePartData>>) => {
      const {tag} = event.detail;
      const {source} = event.detail.data;

      if (!isRegexPart(source) || !hasRegexDisclosure(source)) {
        return;
      }

      setEditedPart({
        bounds: tag.getBoundingClientRect(),
        part: source,
        tag,
      });
    },
    [setEditedPart],
  );

  const onCloseEditPart = useCallback(() => setEditedPart(undefined), [setEditedPart]);

  const onRegexPartUpdate = useCallback(
    (part: RegexPart) => {
      if (!editedPart) {
        return;
      }

      tagifyRef.current?.replaceTag(editedPart.tag, renderTemplatePart(part, expressions));
      setEditedPart(undefined);
    },
    [tagifyRef.current, editedPart, expressions],
  );

  const renderedValue = useMemo(() => renderTagify(value, expressions), [value, expressions]);

  return (
    <StyledInputWrapper>
      <StringTemplateDropdown
        expressions={validExpressions}
        onPartSelected={onPartSelected}
        canUsePreviousResult={stringParameter.canUsePreviousResult}
        canUseRegex={stringParameter.canUseRegex}
        isDisabled={isDisabled}
        onSelectCreateExpression={onSelectCreateExpression}
      >
        <StyledMixedTags<TemplatePartData>
          className={makeClassName(isMultiline)}
          settings={TAGIFY_SETTINGS}
          readOnly={isDisabled}
          onChange={updateModel}
          onClick={onClickTag}
          tagifyRef={tagifyRef}
          placeholder={stringParameter.description || stringParameter.title}
          value={renderedValue}
        />
      </StringTemplateDropdown>
      {editedPart && (
        <RegexPartSettingsPopover
          anchor={() => editedPart.bounds}
          part={editedPart.part}
          onRequestClose={onCloseEditPart}
          onPartUpdate={onRegexPartUpdate}
        />
      )}
    </StyledInputWrapper>
  );
};

/*
 * Helpers.
 */

function makeClassName(isMultiline: boolean) {
  const modifier = isMultiline ? 'multiline' : 'singleline';
  return `inputStringTemplate ${modifier}`;
}

function buildValidExpressions(atom: ClientAtom | undefined, expressions: ReadonlyArray<Expression>) {
  const allowedExpressions = atom ? filterAllowedExpressions(expressions, atom, true) : expressions;
  return filterStringExpressions(allowedExpressions, true);
}
